import { Dialog, DialogContent } from '../../../../common/components/ui/dialog';
import { abortUserInputForCart } from '../../actions/cartActions';
import { CartDialogCartReservationExpired } from './CartDialogCartReservationExpired';
import { CartDialogError } from './CartDialogError';
import { CartDialogMessages } from './CartDialogMessages';
import { CartDialogBlockedPositions } from './CartDialogBlockedPositions';
import { UnauthorizedDialogDisplay } from '../../../../shell/components/UnauthorizedDialogDisplay';
import type { ReactNode } from 'react';
import { CartDialogPositionError } from './CartDialogPositionError';
import { CartDialogOciSessionError } from './CartDialogOciSessionError.tsx';
import { CartDialogFailure } from './CartDialogFailure.tsx';
import { NotCurrentCartDialog } from './NotCurrentCartDialog';
import { CartDialogProlixProApplicationError } from './CartDialogProlixProApplicationError';
import { CartDialogImportCsv } from './CartDialogImportCsv.tsx';
import { getLogger } from '../../../../logging/logger';
import { type CartErrorDialogDescriptor } from '../../state-hooks/useCartErrorNotification';

const clientLog = getLogger('cart-error-dialog');

type CartErrorDialogProp = {
  errorDialogContentDescriptor: CartErrorDialogDescriptor;
};

export function CartErrorDialog({ errorDialogContentDescriptor }: Readonly<CartErrorDialogProp>) {

  if (!errorDialogContentDescriptor) {
    return null;
  }

  let dialogContent: ReactNode;
  let showCloseButton = false;
  let dialogCloseHandler: ((open: boolean) => void) | undefined = undefined;

  switch (errorDialogContentDescriptor) {
    case 'CART_FAILURE':
      dialogContent = <CartDialogFailure />;
      break;
    case 'BLOCKED_POSITIONS':
      dialogContent = <CartDialogBlockedPositions />;
      break;
    case 'NOT_CURRENT_CART':
      dialogContent = <NotCurrentCartDialog />;
      break;
    case 'OCI_SESSION_ERROR':
      dialogContent = <CartDialogOciSessionError />;
      break;
    case 'PROLIX_APPLICATION_ERROR':
      dialogContent = <CartDialogProlixProApplicationError />;
      break;
    case 'GENERIC_CART_ERROR':
      dialogContent = <CartDialogError />;
      break;
    case 'LOGIN_EXPIRED':
      return <UnauthorizedDialogDisplay dialogOpen={true} />;
    case 'CART_EXPIRED':
      dialogContent = <CartDialogCartReservationExpired />;
      break;
    case 'CSV_IMPORT_ERROR':
      dialogContent = <CartDialogImportCsv />;
      showCloseButton = true;
      dialogCloseHandler = abortUserInputForCart;
      break;
    case 'POSITION_INTENT_ERROR':
      dialogContent = <CartDialogPositionError />;
      showCloseButton = true;
      dialogCloseHandler = abortUserInputForCart;
      break;
    case 'BACKEND_MESSAGE':
      dialogContent = <CartDialogMessages />;
      break;
    default: {
      clientLog.error('CartDialog: unhandled dialogContentDescriptor:', errorDialogContentDescriptor);
      dialogContent = <CartDialogError />;
    }
  }

  function handleClose(open: boolean) {
    if (!open && dialogCloseHandler) {
      dialogCloseHandler(open);
    }
  }

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent
        showFullscreenOnMobile
        className="max-w-5xl desktop:max-w-2xl w-full"
        showClose={showCloseButton}
        aria-describedby={undefined} // unterdrückt Warnung in Browser Dev Console
        data-testid={'cart-dialog'}
      >
        {dialogContent}
      </DialogContent>
    </Dialog>
  );
}
